// Import React and hooks for managing component state and lifecycle
import React, { useState, useEffect } from 'react';
// Import icon components from Lucide React icon library
import { Bell, BellOff, Clock, Calendar } from 'lucide-react';

// Define the structure of notification preferences that users can configure
// This interface ensures type safety when working with user preferences
interface NotificationPreferences {
  enableNotifications: boolean;         // Master switch for all notifications
  enableToasts: boolean;               // In-app notification toasts
  enableBrowserNotifications: boolean; // Desktop browser notifications
  overdueReminders: boolean;           // Alerts for overdue deadlines
  todayReminders: boolean;             // Alerts for deadlines due today
  upcomingDays: number;                // How many days in advance to show alerts (0-7)
}

// Define props that this settings component expects
interface NotificationSettingsProps {
  // userId: Unique identifier for the current user (used for storing preferences)
  userId: string;
  // onSave: Optional callback function called when user saves their preferences
  // The ? makes this optional - parent components may or may not provide this function
  onSave?: (preferences: NotificationPreferences) => void;
}

// NotificationSettings component provides a user interface for configuring notification preferences
// Users can enable/disable different types of notifications and set reminder timing
export function NotificationSettings({ userId, onSave }: NotificationSettingsProps) {
  // State to store the user's notification preferences
  // useState with default values - these are the initial settings for new users
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    enableNotifications: true,        // Enable notifications by default
    enableToasts: true,              // Enable in-app toasts by default
    enableBrowserNotifications: false, // Browser notifications disabled by default (requires permission)
    overdueReminders: true,          // Alert for overdue items by default
    todayReminders: true,            // Alert for today's deadlines by default
    upcomingDays: 1,                 // Default to 1 day advance warning
  });

  // useEffect runs when component mounts or when userId changes
  // This loads the user's saved preferences from browser storage
  useEffect(() => {
    // Load preferences from localStorage (browser's local storage)
    // Each user has their own preferences stored with their unique ID
    const saved = localStorage.getItem(`notification-prefs-${userId}`);
    if (saved) {
      // Parse the JSON string back into an object and update state
      // JSON.parse converts the stored string back to a JavaScript object
      setPreferences(JSON.parse(saved));
    }
    // The [userId] dependency means this runs again if the user changes
  }, [userId]);

  // Function to save the user's notification preferences
  const handleSave = () => {
    // Save preferences to localStorage for persistence across browser sessions
    // JSON.stringify converts the preferences object to a string for storage
    localStorage.setItem(`notification-prefs-${userId}`, JSON.stringify(preferences));
    
    // Request browser notification permission if user enabled browser notifications
    // Check if browser supports notifications and user has enabled the feature
    if (preferences.enableBrowserNotifications && 'Notification' in window) {
      // This will show a browser permission dialog to the user
      Notification.requestPermission();
    }
    
    // Call the optional onSave callback if provided by parent component
    // The ?. is optional chaining - only calls the function if it exists
    onSave?.(preferences);
  };

  // Function to toggle boolean preference values (true/false settings)
  // keyof NotificationPreferences ensures we only pass valid preference keys
  const handleToggle = (key: keyof NotificationPreferences) => {
    setPreferences(prev => ({
      ...prev,           // Keep all existing preferences
      [key]: !prev[key]  // Toggle the specific preference (true becomes false, false becomes true)
    }));
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
          Deadline Notifications
        </h3>
        
        <div className="space-y-4">
          {/* Master Toggle */}
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {preferences.enableNotifications ? (
                <Bell className="h-5 w-5 text-gray-400 mr-3" />
              ) : (
                <BellOff className="h-5 w-5 text-gray-400 mr-3" />
              )}
              <div>
                <label htmlFor="enable-notifications" className="font-medium text-gray-700">
                  Enable Notifications
                </label>
                <p className="text-sm text-gray-500">
                  Receive alerts for upcoming and overdue deadlines
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => handleToggle('enableNotifications')}
              className={`${
                preferences.enableNotifications ? 'bg-indigo-600' : 'bg-gray-200'
              } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
            >
              <span
                className={`${
                  preferences.enableNotifications ? 'translate-x-5' : 'translate-x-0'
                } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
              />
            </button>
          </div>

          {preferences.enableNotifications && (
            <>
              {/* Toast Notifications */}
              <div className="ml-8 flex items-center justify-between">
                <div>
                  <label htmlFor="enable-toasts" className="font-medium text-gray-700">
                    In-App Notifications
                  </label>
                  <p className="text-sm text-gray-500">
                    Show notification toasts within the application
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleToggle('enableToasts')}
                  className={`${
                    preferences.enableToasts ? 'bg-indigo-600' : 'bg-gray-200'
                  } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
                >
                  <span
                    className={`${
                      preferences.enableToasts ? 'translate-x-5' : 'translate-x-0'
                    } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                  />
                </button>
              </div>

              {/* Browser Notifications */}
              <div className="ml-8 flex items-center justify-between">
                <div>
                  <label htmlFor="enable-browser" className="font-medium text-gray-700">
                    Browser Notifications
                  </label>
                  <p className="text-sm text-gray-500">
                    Receive desktop notifications even when the app is in the background
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleToggle('enableBrowserNotifications')}
                  className={`${
                    preferences.enableBrowserNotifications ? 'bg-indigo-600' : 'bg-gray-200'
                  } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
                >
                  <span
                    className={`${
                      preferences.enableBrowserNotifications ? 'translate-x-5' : 'translate-x-0'
                    } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                  />
                </button>
              </div>

              <div className="border-t pt-4 mt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Notification Types</h4>
                
                {/* Overdue Reminders */}
                <div className="ml-8 flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 text-red-500 mr-2" />
                    <label htmlFor="overdue-reminders" className="text-sm text-gray-700">
                      Overdue Deadlines
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleToggle('overdueReminders')}
                    className={`${
                      preferences.overdueReminders ? 'bg-indigo-600' : 'bg-gray-200'
                    } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
                  >
                    <span
                      className={`${
                        preferences.overdueReminders ? 'translate-x-5' : 'translate-x-0'
                      } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                    />
                  </button>
                </div>

                {/* Today Reminders */}
                <div className="ml-8 flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 text-orange-500 mr-2" />
                    <label htmlFor="today-reminders" className="text-sm text-gray-700">
                      Due Today
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleToggle('todayReminders')}
                    className={`${
                      preferences.todayReminders ? 'bg-indigo-600' : 'bg-gray-200'
                    } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
                  >
                    <span
                      className={`${
                        preferences.todayReminders ? 'translate-x-5' : 'translate-x-0'
                      } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                    />
                  </button>
                </div>

                {/* Upcoming Days */}
                <div className="ml-8">
                  <label htmlFor="upcoming-days" className="block text-sm font-medium text-gray-700">
                    Remind me about deadlines
                  </label>
                  <select
                    id="upcoming-days"
                    value={preferences.upcomingDays}
                    onChange={(e) => setPreferences(prev => ({ ...prev, upcomingDays: parseInt(e.target.value) }))}
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                  >
                    <option value={0}>On the day</option>
                    <option value={1}>1 day before</option>
                    <option value={2}>2 days before</option>
                    <option value={3}>3 days before</option>
                    <option value={7}>1 week before</option>
                  </select>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="mt-6">
          <button
            onClick={handleSave}
            className="w-full sm:w-auto px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  );
}